<script setup>
import { computed, onMounted, ref } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import UserWizard from '../components/access/UserWizard.vue'
import { apiErrorMessage, createAccessUser, getAccessRole, getAccessRoles, getAccessUser, getPhysicalStation, getPhysicalStations, updateAccessUser } from '../api'
import { useI18n } from '../i18n'
import { getPlatformPts2Controllers } from '../pts2/controllers.js'
import { hasPermission } from '../session.js'

const route = useRoute()
const router = useRouter()
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const dirty = ref(false)
const roles = ref([])
const stations = ref([])
const user = ref(null)
const editing = computed(() => Boolean(route.params.userId))
const { t } = useI18n()

async function load() {
  loading.value = true
  error.value = ''
  try {
    const requests = [getAccessRoles({ pageSize: 100 }), getPhysicalStations()]
    if (editing.value) requests.push(getAccessUser(route.params.userId))
    const [rolePage, stationPage, userResult] = await Promise.all(requests)
    const [roleDetails, stationDetails] = await Promise.all([
      Promise.all(rolePage.items.map((role) => getAccessRole(role.id).then((value) => value.role))),
      Promise.all((stationPage.stations ?? []).map((station) => getPhysicalStation(station.id))),
    ])
    roles.value = roleDetails
    stations.value = await Promise.all(stationDetails.map(async (station) => {
      if (!station.pts2AdministrationAvailable || !hasPermission('pts2.controllers.read')) return { ...station, pts2Controllers: [] }
      return { ...station, pts2Controllers: await getPlatformPts2Controllers(station.id) }
    }))
    user.value = userResult?.user ?? null
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  } finally {
    loading.value = false
  }
}

async function save(form) {
  saving.value = true
  error.value = ''
  try {
    if (editing.value) {
      await updateAccessUser(user.value.id, {
        displayName: form.displayName, roleId: form.roleId, stationIds: form.stationIds,
        physicalStationIds: form.physicalStationIds, naftaPosInstanceIds: form.naftaPosInstanceIds,
        pts2ControllerIds: form.pts2ControllerIds,
        version: user.value.version,
      })
    } else {
      await createAccessUser(form)
    }
    dirty.value = false
    await router.push({ name: 'access-users' })
  } catch (cause) {
    error.value = cause.code === 'version_conflict'
      ? t('userVersionConflict')
      : apiErrorMessage(t, cause)
  } finally {
    saving.value = false
  }
}

function cancel() {
  router.push({ name: 'access-users' })
}

onBeforeRouteLeave(() => !dirty.value || window.confirm(t('leaveWithoutSaving')))
onMounted(load)
</script>

<template>
  <section class="access-wizard-page">
    <header class="access-page-header"><div><p class="access-breadcrumb">{{ t('users') }} / {{ editing ? t('editAccess') : t('addUser') }}</p><h1>{{ editing ? t('editAccess') : t('addUser') }}</h1></div><button class="button button-secondary" type="button" @click="cancel">{{ t('cancel') }}</button></header>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <p v-if="loading" class="access-loading">{{ t('loadingAccess') }}</p>
    <UserWizard v-else :key="user?.version ?? 'new'" :user="user" :roles="roles" :stations="stations" :saving="saving" @submit="save" @cancel="cancel" @dirty="dirty = true" />
  </section>
</template>
